<template>
  <div class="login-layout">
    <div class="mascot-section hidden-sm-and-down">
      <div class="brand-header">
        <div class="logo-box">
          <el-icon class="logo-icon"><MagicStick /></el-icon>
        </div>
        <span class="brand-name">SeaClaw</span>
      </div>

      <div class="mascots-stage">
        <div class="mascots-container">
          <div
            ref="purpleRef"
            class="character purple-char"
            :style="purpleBodyStyle"
          >
            <div class="eyes-wrapper" :style="purpleEyesWrapperStyle">
              <div
                class="eye-ball"
                :class="{ 'is-blinking': isPurpleBlinking }"
              >
                <div
                  class="pupil"
                  :style="getPupilTransform(purpleEye1Ref, 5, purpleForceLook)"
                  ref="purpleEye1Ref"
                ></div>
              </div>
              <div
                class="eye-ball"
                :class="{ 'is-blinking': isPurpleBlinking }"
              >
                <div
                  class="pupil"
                  :style="getPupilTransform(purpleEye2Ref, 5, purpleForceLook)"
                  ref="purpleEye2Ref"
                ></div>
              </div>
            </div>
          </div>

          <div
            ref="blackRef"
            class="character black-char"
            :style="blackBodyStyle"
          >
            <div class="eyes-wrapper" :style="blackEyesWrapperStyle">
              <div
                class="eye-ball small-eye"
                :class="{ 'is-blinking': isBlackBlinking }"
              >
                <div
                  class="pupil small-pupil"
                  :style="getPupilTransform(blackEye1Ref, 4, blackForceLook)"
                  ref="blackEye1Ref"
                ></div>
              </div>
              <div
                class="eye-ball small-eye"
                :class="{ 'is-blinking': isBlackBlinking }"
              >
                <div
                  class="pupil small-pupil"
                  :style="getPupilTransform(blackEye2Ref, 4, blackForceLook)"
                  ref="blackEye2Ref"
                ></div>
              </div>
            </div>
          </div>

          <div
            ref="orangeRef"
            class="character orange-char"
            :style="orangeBodyStyle"
          >
            <div class="eyes-wrapper" :style="orangeEyesWrapperStyle">
              <div
                class="pupil medium-pupil"
                :style="getPupilTransform(orangeEye1Ref, 5, orangeForceLook)"
                ref="orangeEye1Ref"
              ></div>
              <div
                class="pupil medium-pupil"
                :style="getPupilTransform(orangeEye2Ref, 5, orangeForceLook)"
                ref="orangeEye2Ref"
              ></div>
            </div>
          </div>

          <div
            ref="yellowRef"
            class="character yellow-char"
            :style="yellowBodyStyle"
          >
            <div class="eyes-wrapper" :style="yellowEyesWrapperStyle">
              <div
                class="pupil medium-pupil"
                :style="getPupilTransform(yellowEye1Ref, 5, yellowForceLook)"
                ref="yellowEye1Ref"
              ></div>
              <div
                class="pupil medium-pupil"
                :style="getPupilTransform(yellowEye2Ref, 5, yellowForceLook)"
                ref="yellowEye2Ref"
              ></div>
            </div>
            <div class="mouth" :style="yellowMouthStyle"></div>
          </div>
        </div>
      </div>

      <div class="decorative-grid"></div>
      <div class="glow-orb top-orb"></div>
      <div class="glow-orb bottom-orb"></div>
    </div>

    <div class="form-section">
      <div class="form-wrapper">
        <div class="mobile-brand hidden-md-and-up">
          <div class="logo-box-mobile">
            <el-icon><MagicStick /></el-icon>
          </div>
          <span>SeaClaw</span>
        </div>

        <div class="form-header">
          <h1>
            {{
              currentMode === "login"
                ? "欢迎回来"
                : currentMode === "register"
                ? "注册账号"
                : "忘记密码"
            }}
          </h1>
          <p>
            {{
              currentMode === "login"
                ? "继续探索 SeaClaw 的无限可能"
                : currentMode === "register"
                ? "加入 SeaClaw 掌控 AI 智能体"
                : "验证邮箱以设置您的新密码"
            }}
          </p>
        </div>

        <form
          v-if="currentMode === 'login'"
          @submit.prevent="handleLogin"
          class="auth-form"
        >
          <div class="form-group">
            <label>邮箱</label>
            <el-input
              v-model="loginForm.email"
              type="email"
              placeholder="admin@seaclaw.local"
              size="large"
              class="custom-input"
              @focus="handleTypingFocus(true)"
              @blur="handleTypingFocus(false)"
            />
          </div>

          <div class="form-group">
            <label>密码</label>
            <el-input
              v-model="loginForm.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="••••••••"
              size="large"
              class="custom-input"
            >
              <template #suffix>
                <el-icon
                  class="cursor-pointer"
                  @click="showPassword = !showPassword"
                >
                  <View v-if="showPassword" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </div>

          <div class="form-actions">
            <a
              href="#"
              class="forgot-link"
              @click.prevent="switchMode('forgot')"
              >忘记密码?</a
            >
          </div>

          <el-alert
            v-if="errorMessage"
            :title="errorMessage"
            type="error"
            show-icon
            :closable="false"
            class="error-alert"
          />

          <el-button
            type="primary"
            size="large"
            native-type="submit"
            class="submit-btn"
            :loading="isLoading"
          >
            {{ isLoading ? "登录中..." : "登录" }}
          </el-button>
        </form>

        <form
          v-else-if="currentMode === 'register'"
          @submit.prevent="handleRegister"
          class="auth-form"
        >
          <div class="form-group">
            <label>用户名</label>
            <el-input
              v-model="registerForm.username"
              type="text"
              placeholder="2-20个字符，支持中文"
              size="large"
              class="custom-input"
              :class="{ 'is-error': registerErrors.username }"
              @blur="validateRegisterField('username')"
              @input="clearRegisterError('username')"
            />
            <div v-if="registerErrors.username" class="field-error">
              {{ registerErrors.username }}
            </div>
          </div>

          <div class="form-group">
            <label>邮箱</label>
            <el-input
              v-model="registerForm.email"
              type="email"
              placeholder="用于接收验证码"
              size="large"
              class="custom-input"
              :class="{ 'is-error': registerErrors.email }"
              @blur="validateRegisterField('email')"
              @input="clearRegisterError('email')"
            />
            <div v-if="registerErrors.email" class="field-error">
              {{ registerErrors.email }}
            </div>
          </div>

          <div class="form-group">
            <label>验证码</label>
            <div class="code-input-group">
              <el-input
                v-model="registerForm.code"
                placeholder="4位验证码"
                size="large"
                class="custom-input code-input"
                :class="{ 'is-error': registerErrors.code }"
                maxlength="4"
                @blur="validateRegisterField('code')"
                @input="clearRegisterError('code')"
              />
              <el-button
                size="large"
                type="primary"
                plain
                class="send-code-btn"
                :disabled="countdown > 0"
                @click="sendVerificationCode"
                :loading="isSendingCode"
              >
                {{ countdown > 0 ? `${countdown}s 后重新获取` : "获取验证码" }}
              </el-button>
            </div>
            <div v-if="registerErrors.code" class="field-error">
              {{ registerErrors.code }}
            </div>
          </div>

          <div class="form-group">
            <label>密码</label>
            <el-input
              v-model="registerForm.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="至少6位密码"
              size="large"
              class="custom-input"
              :class="{ 'is-error': registerErrors.password }"
              @focus="handleTypingFocus(true)"
              @blur="
                () => {
                  handleTypingFocus(false);
                  validateRegisterField('password');
                }
              "
              @input="clearRegisterError('password')"
            >
              <template #suffix>
                <el-icon
                  class="cursor-pointer"
                  @click="showPassword = !showPassword"
                >
                  <View v-if="showPassword" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
            <div v-if="registerErrors.password" class="field-error">
              {{ registerErrors.password }}
            </div>
          </div>

          <div class="form-group">
            <label>确认密码</label>
            <el-input
              v-model="registerForm.confirm_password"
              type="password"
              placeholder="请再次输入密码"
              size="large"
              class="custom-input"
              :class="{ 'is-error': registerErrors.confirm_password }"
              @blur="validateRegisterField('confirm_password')"
              @input="clearRegisterError('confirm_password')"
            />
            <div v-if="registerErrors.confirm_password" class="field-error">
              {{ registerErrors.confirm_password }}
            </div>
          </div>

          <el-alert
            v-if="errorMessage"
            :title="errorMessage"
            type="error"
            show-icon
            :closable="false"
            class="error-alert"
          />

          <el-button
            type="primary"
            size="large"
            native-type="submit"
            class="submit-btn"
            :loading="isLoading"
          >
            {{ isLoading ? "注册中..." : "立即注册" }}
          </el-button>
        </form>

        <form
          v-else-if="currentMode === 'forgot'"
          @submit.prevent="handleResetPassword"
          class="auth-form"
        >
          <div class="form-group">
            <label>注册邮箱</label>
            <el-input
              v-model="forgotForm.email"
              type="email"
              placeholder="请输入您注册时的邮箱"
              size="large"
              class="custom-input"
            />
          </div>

          <div class="form-group">
            <label>验证码</label>
            <div class="code-input-group">
              <el-input
                v-model="forgotForm.code"
                placeholder="4位验证码"
                size="large"
                class="custom-input code-input"
                maxlength="4"
              />
              <el-button
                size="large"
                type="primary"
                plain
                class="send-code-btn"
                :disabled="countdown > 0"
                @click="sendVerificationCode"
                :loading="isSendingCode"
              >
                {{ countdown > 0 ? `${countdown}s 后重新获取` : "获取验证码" }}
              </el-button>
            </div>
          </div>

          <div class="form-group">
            <label>新密码</label>
            <el-input
              v-model="forgotForm.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="请输入新密码"
              size="large"
              class="custom-input"
              @focus="handleTypingFocus(true)"
              @blur="handleTypingFocus(false)"
            >
              <template #suffix>
                <el-icon
                  class="cursor-pointer"
                  @click="showPassword = !showPassword"
                >
                  <View v-if="showPassword" />
                  <Hide v-else />
                </el-icon>
              </template>
            </el-input>
          </div>

          <div class="form-group">
            <label>确认新密码</label>
            <el-input
              v-model="forgotForm.confirm_password"
              type="password"
              placeholder="请再次输入新密码"
              size="large"
              class="custom-input"
            />
          </div>

          <el-alert
            v-if="errorMessage"
            :title="errorMessage"
            type="error"
            show-icon
            :closable="false"
            class="error-alert"
          />

          <el-button
            type="primary"
            size="large"
            native-type="submit"
            class="submit-btn"
            :loading="isLoading"
          >
            {{ isLoading ? "提交中..." : "重置密码" }}
          </el-button>
        </form>

        <div class="mode-switch-wrapper">
          <p class="mode-text" v-if="currentMode === 'login'">
            还没有账号？
            <a
              href="#"
              @click.prevent="switchMode('register')"
              class="mode-link"
              >立即注册</a
            >
          </p>
          <p class="mode-text" v-else>
            想起密码了？
            <a href="#" @click.prevent="switchMode('login')" class="mode-link"
              >返回登录</a
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { View, Hide, MagicStick } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { useAuthStore } from "@/stores/auth";

const router = useRouter();
const authStore = useAuthStore();

// ================= 1. 表单与状态控制 =================
// 状态重构：用字符串管理三种状态 'login' | 'register' | 'forgot'
const currentMode = ref("login");
const isLoading = ref(false);
const errorMessage = ref("");
const showPassword = ref(false);
const isTyping = ref(false);

const loginForm = reactive({
  email: "1534441778@qq.com",
  password: "123456",
});

const registerForm = reactive({
  username: "",
  email: "",
  code: "",
  password: "",
  confirm_password: "",
});

const registerErrors = reactive({
  username: "",
  email: "",
  code: "",
  password: "",
  confirm_password: "",
});

const validateRegisterField = (field) => {
  const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  switch (field) {
    case "username":
      if (!registerForm.username) registerErrors.username = "用户名不能为空";
      else if (registerForm.username.length < 2)
        registerErrors.username = "用户名至少需要2个字符";
      else if (registerForm.username.length > 20)
        registerErrors.username = "用户名不能超过20个字符";
      else registerErrors.username = "";
      break;
    case "email":
      if (!registerForm.email) registerErrors.email = "邮箱不能为空";
      else if (!emailReg.test(registerForm.email))
        registerErrors.email = "请输入正确的邮箱格式";
      else registerErrors.email = "";
      break;
    case "code":
      if (!registerForm.code) registerErrors.code = "验证码不能为空";
      else if (registerForm.code.length !== 4)
        registerErrors.code = "验证码为4位数字";
      else registerErrors.code = "";
      break;
    case "password":
      if (!registerForm.password) registerErrors.password = "密码不能为空";
      else if (registerForm.password.length < 6)
        registerErrors.password = "密码至少需要6位";
      else registerErrors.password = "";
      // 同步刷新确认密码的校验结果
      if (registerForm.confirm_password)
        validateRegisterField("confirm_password");
      break;
    case "confirm_password":
      if (!registerForm.confirm_password)
        registerErrors.confirm_password = "请再次输入密码";
      else if (registerForm.confirm_password !== registerForm.password)
        registerErrors.confirm_password = "两次输入的密码不一致";
      else registerErrors.confirm_password = "";
      break;
  }
};

const clearRegisterError = (field) => {
  if (registerErrors[field]) validateRegisterField(field);
};

const forgotForm = reactive({
  email: "",
  code: "",
  password: "",
  confirm_password: "",
});

// 验证码倒计时相关
const countdown = ref(0);
const isSendingCode = ref(false);
let timer = null;

const switchMode = (mode) => {
  currentMode.value = mode;
  errorMessage.value = "";
  showPassword.value = false;
};

// ================= 2. API 接口逻辑 =================
const API_BASE = "/api/auth";

// 发送验证码（智能判断读取哪个邮箱）
const sendVerificationCode = async () => {
  const targetEmail =
    currentMode.value === "register" ? registerForm.email : forgotForm.email;

  if (!targetEmail || !targetEmail.includes("@")) {
    errorMessage.value = "请先填写正确的邮箱地址！";
    return;
  }

  errorMessage.value = "";
  isSendingCode.value = true;

  try {
    const res = await axios.get(`${API_BASE}/code`, {
      params: { email: targetEmail },
    });

    ElMessage.success(res.data.msg || "验证码已发送，请查收邮箱！");

    // 启动 60 秒倒计时
    countdown.value = 60;
    timer = setInterval(() => {
      countdown.value--;
      if (countdown.value <= 0) {
        clearInterval(timer);
      }
    }, 1000);
  } catch (error) {
    errorMessage.value =
      error.response?.data?.detail || "发送验证码失败，请稍后重试。";
  } finally {
    isSendingCode.value = false;
  }
};

// 处理注册
const handleRegister = async () => {
  if (registerForm.password !== registerForm.confirm_password) {
    errorMessage.value = "两次输入的密码不一致！";
    return;
  }
  if (!registerForm.code || registerForm.code.length !== 4) {
    errorMessage.value = "请输入 4 位验证码！";
    return;
  }

  errorMessage.value = "";
  isLoading.value = true;

  try {
    const res = await axios.post(`${API_BASE}/register`, {
      email: registerForm.email,
      username: registerForm.username,
      password: registerForm.password,
      code: registerForm.code,
      confirm_password: registerForm.confirm_password,
    });

    ElMessage.success(res.data.msg || "注册成功！请登录。");
    // 自动填充并切回登录页
    loginForm.email = registerForm.email;
    loginForm.password = "";
    switchMode("login");
  } catch (error) {
    errorMessage.value =
      error.response?.data?.detail || "注册失败，请检查填写信息。";
  } finally {
    isLoading.value = false;
  }
};

// 处理重置密码
const handleResetPassword = async () => {
  if (forgotForm.password !== forgotForm.confirm_password) {
    errorMessage.value = "两次输入的新密码不一致！";
    return;
  }
  if (!forgotForm.code || forgotForm.code.length !== 4) {
    errorMessage.value = "请输入 4 位验证码！";
    return;
  }
  if (forgotForm.password.length < 6) {
    errorMessage.value = "密码长度至少需要 6 位！";
    return;
  }

  errorMessage.value = "";
  isLoading.value = true;

  try {
    // 这里的参数严格对应后端的 ResetPasswordIn schema
    const res = await axios.post(`${API_BASE}/reset-password`, {
      email: forgotForm.email,
      code: forgotForm.code,
      new_password: forgotForm.password,
    });

    ElMessage.success(res.data.message || "密码重置成功！请使用新密码登录。");

    // 自动填充并切回登录页
    loginForm.email = forgotForm.email;
    loginForm.password = "";
    switchMode("login");
  } catch (error) {
    errorMessage.value =
      error.response?.data?.detail || "重置失败，请检查验证码是否正确。";
  } finally {
    isLoading.value = false;
  }
};

// 处理登录
const handleLogin = async () => {
  if (!loginForm.email.includes("@") || loginForm.password.length < 6) {
    errorMessage.value = "邮箱格式不正确或密码过短。";
    return;
  }

  errorMessage.value = "";
  isLoading.value = true;

  try {
    const res = await axios.post(`${API_BASE}/login`, {
      email: loginForm.email,
      password: loginForm.password,
    });

    const token = res.data.data.access_token;
    authStore.setToken(token);

    ElMessage.success({ message: "登录成功", duration: 2000 });
    router.push("/console/chat");
  } catch (error) {
    errorMessage.value =
      error.response?.data?.detail || "登录失败，请检查账号密码。";
  } finally {
    isLoading.value = false;
  }
};

// ================= 3. 动画核心状态与计算 =================
const mouse = reactive({ x: 0, y: 0 });
const isLookingAtEachOther = ref(false);
const isPurpleBlinking = ref(false);
const isBlackBlinking = ref(false);
const isPurplePeeking = ref(false);

const purpleRef = ref(null);
const blackRef = ref(null);
const orangeRef = ref(null);
const yellowRef = ref(null);

const purpleEye1Ref = ref(null);
const purpleEye2Ref = ref(null);
const blackEye1Ref = ref(null);
const blackEye2Ref = ref(null);
const orangeEye1Ref = ref(null);
const orangeEye2Ref = ref(null);
const yellowEye1Ref = ref(null);
const yellowEye2Ref = ref(null);

const handleMouseMove = (e) => {
  mouse.x = e.clientX;
  mouse.y = e.clientY;
};

onMounted(() => {
  window.addEventListener("mousemove", handleMouseMove);
  startBlinkingCycles();
});

onUnmounted(() => {
  window.removeEventListener("mousemove", handleMouseMove);
  if (timer) clearInterval(timer);
});

const getCharacterPos = (elRef) => {
  if (!elRef.value) return { faceX: 0, faceY: 0, bodySkew: 0 };
  const rect = elRef.value.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 3;
  const deltaX = mouse.x - centerX;
  const deltaY = mouse.y - centerY;
  const faceX = Math.max(-15, Math.min(15, deltaX / 20));
  const faceY = Math.max(-10, Math.min(10, deltaY / 30));
  const bodySkew = Math.max(-6, Math.min(6, -deltaX / 120));
  return { faceX, faceY, bodySkew };
};

const getPupilTransform = (eyeEl, maxDistance, forceLook) => {
  if (forceLook && forceLook.x !== undefined && forceLook.y !== undefined) {
    return `transform: translate(${forceLook.x}px, ${forceLook.y}px)`;
  }
  if (!eyeEl) return `transform: translate(0px, 0px)`;
  const rect = eyeEl.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const deltaX = mouse.x - centerX;
  const deltaY = mouse.y - centerY;
  const distance = Math.min(Math.sqrt(deltaX ** 2 + deltaY ** 2), maxDistance);
  const angle = Math.atan2(deltaY, deltaX);
  const x = Math.cos(angle) * distance;
  const y = Math.sin(angle) * distance;
  return `transform: translate(${x}px, ${y}px)`;
};

// 动画适配重写：支持三种模式的密码长度监听
const currentPasswordLength = computed(() => {
  if (currentMode.value === "login") return loginForm.password.length;
  if (currentMode.value === "register") return registerForm.password.length;
  return forgotForm.password.length;
});

const purplePos = computed(() => getCharacterPos(purpleRef));
const purpleBodyStyle = computed(() => {
  const isTall =
    isTyping.value || (currentPasswordLength.value > 0 && !showPassword.value);
  const skew =
    currentPasswordLength.value > 0 && showPassword.value
      ? 0
      : isTall
      ? purplePos.value.bodySkew - 12
      : purplePos.value.bodySkew;
  const translateX = isTall ? "40px" : "0px";
  return `height: ${
    isTall ? "440px" : "400px"
  }; transform: skewX(${skew}deg) translateX(${translateX})`;
});

const purpleEyesWrapperStyle = computed(() => {
  const isExposed = currentPasswordLength.value > 0 && showPassword.value;
  const left = isExposed
    ? 20
    : isLookingAtEachOther.value
    ? 55
    : 45 + purplePos.value.faceX;
  const top = isExposed
    ? 35
    : isLookingAtEachOther.value
    ? 65
    : 40 + purplePos.value.faceY;
  return `left: ${left}px; top: ${top}px`;
});

const purpleForceLook = computed(() => {
  if (currentPasswordLength.value > 0 && showPassword.value)
    return {
      x: isPurplePeeking.value ? 4 : -4,
      y: isPurplePeeking.value ? 5 : -4,
    };
  if (isLookingAtEachOther.value) return { x: 3, y: 4 };
  return null;
});

const blackPos = computed(() => getCharacterPos(blackRef));
const blackBodyStyle = computed(() => {
  const skew =
    currentPasswordLength.value > 0 && showPassword.value
      ? 0
      : isLookingAtEachOther.value
      ? blackPos.value.bodySkew * 1.5 + 10
      : isTyping.value ||
        (currentPasswordLength.value > 0 && !showPassword.value)
      ? blackPos.value.bodySkew * 1.5
      : blackPos.value.bodySkew;
  const translateX = isLookingAtEachOther.value ? "20px" : "0px";
  return `transform: skewX(${skew}deg) translateX(${translateX})`;
});

const blackEyesWrapperStyle = computed(() => {
  const isExposed = currentPasswordLength.value > 0 && showPassword.value;
  const left = isExposed
    ? 10
    : isLookingAtEachOther.value
    ? 32
    : 26 + blackPos.value.faceX;
  const top = isExposed
    ? 28
    : isLookingAtEachOther.value
    ? 12
    : 32 + blackPos.value.faceY;
  return `left: ${left}px; top: ${top}px`;
});

const blackForceLook = computed(() => {
  if (currentPasswordLength.value > 0 && showPassword.value)
    return { x: -4, y: -4 };
  if (isLookingAtEachOther.value) return { x: 0, y: -4 };
  return null;
});

const orangePos = computed(() => getCharacterPos(orangeRef));
const orangeBodyStyle = computed(() => {
  const skew =
    currentPasswordLength.value > 0 && showPassword.value
      ? 0
      : orangePos.value.bodySkew;
  return `transform: skewX(${skew}deg)`;
});
const orangeEyesWrapperStyle = computed(() => {
  const isExposed = currentPasswordLength.value > 0 && showPassword.value;
  const left = isExposed ? 50 : 82 + orangePos.value.faceX;
  const top = isExposed ? 85 : 90 + orangePos.value.faceY;
  return `left: ${left}px; top: ${top}px`;
});
const orangeForceLook = computed(() => {
  if (currentPasswordLength.value > 0 && showPassword.value)
    return { x: -5, y: -4 };
  return null;
});

const yellowPos = computed(() => getCharacterPos(yellowRef));
const yellowBodyStyle = computed(() => {
  const skew =
    currentPasswordLength.value > 0 && showPassword.value
      ? 0
      : yellowPos.value.bodySkew;
  return `transform: skewX(${skew}deg)`;
});
const yellowEyesWrapperStyle = computed(() => {
  const isExposed = currentPasswordLength.value > 0 && showPassword.value;
  const left = isExposed ? 20 : 52 + yellowPos.value.faceX;
  const top = isExposed ? 35 : 40 + yellowPos.value.faceY;
  return `left: ${left}px; top: ${top}px`;
});
const yellowMouthStyle = computed(() => {
  const isExposed = currentPasswordLength.value > 0 && showPassword.value;
  const left = isExposed ? 10 : 40 + yellowPos.value.faceX;
  const top = isExposed ? 88 : 88 + yellowPos.value.faceY;
  return `left: ${left}px; top: ${top}px`;
});
const yellowForceLook = computed(() => {
  if (currentPasswordLength.value > 0 && showPassword.value)
    return { x: -5, y: -4 };
  return null;
});

const handleTypingFocus = (status) => {
  isTyping.value = status;
  if (status) {
    isLookingAtEachOther.value = true;
    setTimeout(() => {
      isLookingAtEachOther.value = false;
    }, 800);
  }
};

watch(
  () => showPassword.value,
  (newVal) => {
    if (newVal && currentPasswordLength.value > 0) {
      startPeekingCycle();
    } else {
      isPurplePeeking.value = false;
    }
  },
);

let peekingTimer = null;
const startPeekingCycle = () => {
  clearTimeout(peekingTimer);
  if (!showPassword.value || currentPasswordLength.value === 0) return;
  peekingTimer = setTimeout(() => {
    isPurplePeeking.value = true;
    setTimeout(() => {
      isPurplePeeking.value = false;
    }, 800);
    startPeekingCycle();
  }, Math.random() * 3000 + 2000);
};

const startBlinkingCycles = () => {
  const schedulePurpleBlink = () => {
    setTimeout(() => {
      isPurpleBlinking.value = true;
      setTimeout(() => {
        isPurpleBlinking.value = false;
        schedulePurpleBlink();
      }, 150);
    }, Math.random() * 4000 + 3000);
  };
  const scheduleBlackBlink = () => {
    setTimeout(() => {
      isBlackBlinking.value = true;
      setTimeout(() => {
        isBlackBlinking.value = false;
        scheduleBlackBlink();
      }, 150);
    }, Math.random() * 4000 + 3000);
  };
  schedulePurpleBlink();
  scheduleBlackBlink();
};
</script>

<style scoped>
/* 原有样式保持不变，无需修改任何一行 */
.login-layout {
  display: flex;
  min-height: 100vh;
  width: 100vw;
  background-color: #ffffff;
  overflow: hidden;
}

.mascot-section {
  flex: 1;
  position: relative;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  padding: 48px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
}

.brand-header {
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ffffff;
  font-size: 20px;
  font-weight: bold;
}

.logo-box {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-icon {
  font-size: 20px;
}
.mascots-stage {
  position: relative;
  z-index: 20;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 500px;
}
.mascots-container {
  position: relative;
  width: 550px;
  height: 400px;
}
.character {
  position: absolute;
  bottom: 0;
  transform-origin: bottom center;
  transition: transform 0.7s cubic-bezier(0.25, 0.8, 0.25, 1), height 0.7s ease;
}
.eyes-wrapper {
  position: absolute;
  display: flex;
  transition: all 0.7s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.eye-ball {
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: height 0.15s ease-out;
}
.eye-ball.is-blinking {
  height: 2px !important;
}
.pupil {
  background-color: #2d2d2d;
  border-radius: 50%;
  transition: transform 0.1s ease-out;
}

.purple-char {
  left: 70px;
  width: 180px;
  background-color: #6c3ff5;
  border-radius: 10px 10px 0 0;
  z-index: 1;
}
.purple-char .eyes-wrapper {
  gap: 32px;
}
.purple-char .eye-ball {
  width: 18px;
  height: 18px;
}
.purple-char .pupil {
  width: 7px;
  height: 7px;
}

.black-char {
  left: 240px;
  width: 120px;
  height: 310px;
  background-color: #2d2d2d;
  border-radius: 8px 8px 0 0;
  z-index: 2;
}
.black-char .eyes-wrapper {
  gap: 24px;
}
.black-char .small-eye {
  width: 16px;
  height: 16px;
}
.black-char .small-pupil {
  width: 6px;
  height: 6px;
}

.orange-char {
  left: 0px;
  width: 240px;
  height: 200px;
  background-color: #ff9b6b;
  border-radius: 120px 120px 0 0;
  z-index: 3;
}
.orange-char .eyes-wrapper {
  gap: 32px;
  transition-duration: 0.2s;
}
.orange-char .medium-pupil {
  width: 12px;
  height: 12px;
}

.yellow-char {
  left: 310px;
  width: 140px;
  height: 230px;
  background-color: #e8d754;
  border-radius: 70px 70px 0 0;
  z-index: 4;
}
.yellow-char .eyes-wrapper {
  gap: 24px;
  transition-duration: 0.2s;
}
.yellow-char .medium-pupil {
  width: 12px;
  height: 12px;
}
.yellow-char .mouth {
  position: absolute;
  width: 80px;
  height: 4px;
  background-color: #2d2d2d;
  border-radius: 2px;
  transition: all 0.2s ease-out;
}

.decorative-grid {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    rgba(255, 255, 255, 0.1) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
  z-index: 0;
}
.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 0;
}
.top-orb {
  top: 25%;
  right: 25%;
  width: 256px;
  height: 256px;
  background: rgba(255, 255, 255, 0.1);
}
.bottom-orb {
  bottom: 25%;
  left: 25%;
  width: 384px;
  height: 384px;
  background: rgba(255, 255, 255, 0.05);
}

/* ================= 右侧表单区域 ================= */
.form-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background-color: #ffffff;
  overflow-y: auto;
}

.form-wrapper {
  width: 100%;
  max-width: 420px;
  padding: 20px 0;
}

.mobile-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 20px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 48px;
}

.logo-box-mobile {
  width: 36px;
  height: 36px;
  background: rgba(64, 158, 255, 0.1);
  color: #409eff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-header {
  text-align: center;
  margin-bottom: 40px;
}
.form-header h1 {
  font-size: 28px;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}
.form-header p {
  color: #909399;
  font-size: 15px;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 8px;
}

.custom-input :deep(.el-input__wrapper) {
  background-color: #f5f7fa;
  border: 1px solid transparent;
  box-shadow: none;
  transition: all 0.2s;
  padding: 0 15px;
}

.custom-input :deep(.el-input__wrapper.is-focus) {
  background-color: #ffffff;
  border: 1px solid #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.code-input-group {
  display: flex;
  gap: 12px;
}
.code-input {
  flex: 1;
}
.send-code-btn {
  width: 130px;
  border-radius: 8px;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -4px;
}
.forgot-link {
  font-size: 14px;
  color: #409eff;
  text-decoration: none;
  font-weight: 500;
}
.forgot-link:hover {
  text-decoration: underline;
}

.error-alert {
  margin-top: 10px;
  border-radius: 8px;
}

.field-error {
  margin-top: 4px;
  font-size: 12px;
  color: #f56c6c;
  line-height: 1.4;
}

.custom-input.is-error :deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px #f56c6c inset;
}

.submit-btn {
  margin-top: 10px;
  height: 48px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;
}

.mode-switch-wrapper {
  text-align: center;
  margin-top: 24px;
}
.mode-text {
  font-size: 14px;
  color: #606266;
}
.mode-link {
  color: #409eff;
  font-weight: bold;
  text-decoration: none;
  margin-left: 4px;
  transition: color 0.2s;
}
.mode-link:hover {
  color: #66b1ff;
  text-decoration: underline;
}

.social-login {
  margin-top: 24px;
}
.divider {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  color: #c0c4cc;
  font-size: 14px;
}
.divider::before,
.divider::after {
  content: "";
  flex: 1;
  border-top: 1px solid #ebeef5;
}
.divider span {
  padding: 0 15px;
}

.sso-btn {
  width: 100%;
  height: 48px;
  border-radius: 8px;
  color: #606266;
}

@media (max-width: 992px) {
  .hidden-sm-and-down {
    display: none !important;
  }
  .hidden-md-and-up {
    display: flex !important;
  }
}
@media (min-width: 993px) {
  .hidden-md-and-up {
    display: none !important;
  }
}
</style>
